import type { Action, ActionReducerMap } from '@ngrx/store';
import { combineReducers, on } from '@ngrx/store';
import {
  activateFormStep,
  activateProgressiveFormStepOnSubmissionOf,
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
  markAsInert,
  markAsyncTasksAsCompleted,
} from '@innogy/progressive-ngrx-forms';
import {
  addGroupControl,
  onNgrxFormsAction,
  removeGroupControl,
  ResetAction,
  setUserDefinedProperty,
  setValue,
  updateGroup,
  validate,
  type StateUpdateFns,
} from 'ngrx-forms';
import { FehApiActions } from '@feh/shared-api';
import { AddressTypes, type FormAddress } from '@innogy/common-ui/forms';
import {
  isEmailAddress,
  isLastName,
  isNumeric,
  isPhoneNumber,
  isPostalCode,
  useValidatorIf,
  validateSequential,
} from '@innogy/utils-deprecated';
import { maxLength, required } from 'ngrx-forms/validation';

import {
  addCheckboxFormControlAction,
  prefillSolarPanelsOrderFormAddressStateAction,
} from './solar-panels-order-form.actions';
import type {
  FormConfirmation,
  OrderAddressState,
  OrderConfirmationState,
  OrderPersonalDetailsState,
  PersonalDetails,
} from '../models/order-form.interface';

export const solarPanelsOrderFormKey = 'solarPanelsOrderForm';
export const solarPanelsOrderAddressFormId = 'solar-panels-address';
export const solarPanelsOrderPersonalDetailsFormId =
  'solar-panels-personal-details';
export const solarPanelsOrderConfirmationFormId = 'solar-panels-confirmation';

export interface SolarPanelsOrderFormState {
  readonly [solarPanelsOrderAddressFormId]: OrderAddressState;
  readonly [solarPanelsOrderPersonalDetailsFormId]: OrderPersonalDetailsState;
  readonly [solarPanelsOrderConfirmationFormId]: OrderConfirmationState;
}

export const initialOrderAddressState = {
  progressiveForm: createProgressiveFormGroupState<FormAddress>(
    solarPanelsOrderAddressFormId,
    {
      street: '',
      communicationNumber: NaN,
      houseNumberAddition: '',
      postalCode: '',
      city: '',
      countryCode: 'NL',
      addressType: AddressTypes.ADDRESS,
    }
  ),
};

export const initialOrderConfirmationState = {
  progressiveForm: createProgressiveFormGroupState<FormConfirmation>(
    solarPanelsOrderConfirmationFormId,
    {
      checkboxes: {},
    }
  ),
};

export const initialOrderPersonalDetailsState = {
  progressiveForm: createProgressiveFormGroupState<PersonalDetails>(
    solarPanelsOrderPersonalDetailsFormId,
    {
      salutation: undefined,
      initials: '',
      prefix: '',
      lastName: '',
      phoneNumber: '',
      emailAddress: '',
      homeBatteryInterest: false,
    }
  ),
};

export const initialSolarPanelsOrderFormState: SolarPanelsOrderFormState = {
  [solarPanelsOrderAddressFormId]: initialOrderAddressState,
  [solarPanelsOrderPersonalDetailsFormId]: initialOrderPersonalDetailsState,
  [solarPanelsOrderConfirmationFormId]: initialOrderConfirmationState,
};

const isAddress = (addressType: `${AddressTypes}`) =>
  addressType === AddressTypes.ADDRESS;

export const orderAddressFormReducer =
  createProgressiveNgrxFormReducerWithFormStateUpdate(
    initialOrderAddressState,
    (state: OrderAddressState) =>
      updateGroup<FormAddress>({
        postalCode: validateSequential(required, isPostalCode),
        communicationNumber: validateSequential(required, isNumeric),
        houseNumberAddition: validateSequential(
          useValidatorIf(
            maxLength(6),
            isAddress(state.progressiveForm.formState.value.addressType)
          )
        ),
        street: validateSequential(
          useValidatorIf(
            required,
            isAddress(state.progressiveForm.formState.value.addressType)
          )
        ),
        city: validateSequential(required),
      })(state.progressiveForm.formState),
    on(prefillSolarPanelsOrderFormAddressStateAction, (state, action) => {
      const formState = setValue(state.progressiveForm.formState, {
        ...state.progressiveForm.formState.value,
        ...action.payload,
      });
      const progressiveForm = markAsInert({
        ...state.progressiveForm,
        formState,
      });

      return {
        ...state,
        progressiveForm,
      };
    })
  );

export const orderPersonalDetailsFormReducer =
  createProgressiveNgrxFormReducerWithFormStateUpdate(
    initialOrderPersonalDetailsState,
    (state: OrderPersonalDetailsState) =>
      updateGroup<PersonalDetails>({
        salutation: validate(required),
        initials: validate(required),
        lastName: validateSequential(required, isLastName),
        phoneNumber: validateSequential(required, isPhoneNumber),
        emailAddress: validateSequential(required, isEmailAddress),
      })(state.progressiveForm.formState),
    on(prefillSolarPanelsOrderFormAddressStateAction, (state) => ({
      ...state,
      progressiveForm: activateFormStep(state.progressiveForm),
    }))
  );

const validateFormGroupState = (state: OrderConfirmationState) => {
  const { controls, userDefinedProperties } =
    state.progressiveForm.formState.controls.checkboxes;

  const validations = Object.keys(controls).reduce(
    (acc, key) => ({
      ...acc,
      ...(userDefinedProperties[key] && {
        [key]: userDefinedProperties[key],
      }),
    }),
    {}
  );

  return updateGroup<FormConfirmation>({
    checkboxes: updateGroup(validations),
  })(state.progressiveForm.formState);
};

export const orderConfirmationFormReducer =
  createProgressiveNgrxFormReducerWithFormStateUpdate(
    initialOrderConfirmationState,
    validateFormGroupState,
    onNgrxFormsAction(
      ResetAction,
      (state: OrderConfirmationState, action: ResetAction) =>
        action.controlId === solarPanelsOrderConfirmationFormId
          ? initialOrderConfirmationState
          : state
    ),
    on(addCheckboxFormControlAction, (state, { name, validator }) => {
      const controlAlreadyExists =
        !!state.progressiveForm.formState.controls.checkboxes.controls[name];
      const validatorAlreadyExists =
        !!state.progressiveForm.formState.controls.checkboxes
          .userDefinedProperties[name];

      const removeControl: StateUpdateFns<FormConfirmation> =
        controlAlreadyExists
          ? {
              checkboxes: removeGroupControl(name),
            }
          : {};
      const removeValidator: StateUpdateFns<FormConfirmation> =
        validatorAlreadyExists
          ? {
              checkboxes: setUserDefinedProperty(name, undefined),
            }
          : {};
      const addControl: StateUpdateFns<FormConfirmation> = {
        checkboxes: addGroupControl(name, false),
      };
      const addValidator: StateUpdateFns<FormConfirmation> = validator
        ? {
            checkboxes: setUserDefinedProperty(name, validator),
          }
        : {};
      return {
        ...state,
        progressiveForm: {
          ...state.progressiveForm,
          formState: updateGroup<FormConfirmation>(
            state.progressiveForm.formState,
            removeControl,
            removeValidator,
            addControl,
            addValidator
          ),
        },
      };
    }),
    activateProgressiveFormStepOnSubmissionOf(
      solarPanelsOrderPersonalDetailsFormId
    ),
    on(FehApiActions.postSolarpanelV1.successAction, (state) => ({
      ...state,
      progressiveForm: markAsyncTasksAsCompleted(state.progressiveForm),
    }))
  );

export const _reducers: ActionReducerMap<SolarPanelsOrderFormState> = {
  [solarPanelsOrderAddressFormId]: orderAddressFormReducer,
  [solarPanelsOrderPersonalDetailsFormId]: orderPersonalDetailsFormReducer,
  [solarPanelsOrderConfirmationFormId]: orderConfirmationFormReducer,
};

const _combinedReducers = combineReducers(_reducers);

export const solarPanelsOrderFormReducer = (
  state: SolarPanelsOrderFormState = initialSolarPanelsOrderFormState,
  action: Action
): SolarPanelsOrderFormState => _combinedReducers(state, action);
